import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isFullUpdateSpotlightCompleted,
  isWelcomeTourCompleted,
  markFullUpdateSpotlightCompleted,
} from '../onboarding/onboardingState';
import { DOM_EVENT_WELCOME_TOUR_COMPLETED } from '../config/storageKeys';

/** Dispatched from WelcomeTour when the user finishes or skips the tour */
export const WELCOME_TOUR_COMPLETED_EVENT = DOM_EVENT_WELCOME_TOUR_COMPLETED;

function isWelcomeTourOverlayInDom(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector('.welcome-tour-overlay') !== null;
}

export interface UseFullUpdateSpotlightParams {
  orgOnboardingComplete: boolean;
  doneCount: number;
  doneCountLoaded: boolean;
}

export interface UseFullUpdateSpotlightResult {
  spotlightActive: boolean;
  dismissSpotlight: () => void;
}

/**
 * After the welcome tour closes, highlights Full Update on Analytics for first-time empty DB.
 */
export function useFullUpdateSpotlight({
  orgOnboardingComplete,
  doneCount,
  doneCountLoaded,
}: UseFullUpdateSpotlightParams): UseFullUpdateSpotlightResult {
  const [spotlightActive, setSpotlightActive] = useState(false);
  const spotlightActiveRef = useRef(spotlightActive);
  spotlightActiveRef.current = spotlightActive;

  const dismissSpotlight = useCallback(() => {
    markFullUpdateSpotlightCompleted();
    setSpotlightActive(false);
  }, []);

  const eligible = useCallback(() => {
    if (!orgOnboardingComplete) return false;
    if (!isWelcomeTourCompleted()) return false;
    if (isFullUpdateSpotlightCompleted()) return false;
    if (!doneCountLoaded) return false;
    if (doneCount > 0) return false;
    return true;
  }, [orgOnboardingComplete, doneCount, doneCountLoaded]);

  const tryActivate = useCallback(() => {
    if (!eligible()) return;
    if (isWelcomeTourOverlayInDom()) return;
    setSpotlightActive(true);
  }, [eligible]);

  // After tour: delayed activation (lets overlay unmount / paint)
  useEffect(() => {
    const onTourDone = () => {
      window.setTimeout(() => {
        if (isFullUpdateSpotlightCompleted()) return;
        tryActivate();
      }, 300);
    };
    window.addEventListener(WELCOME_TOUR_COMPLETED_EVENT, onTourDone);
    return () => window.removeEventListener(WELCOME_TOUR_COMPLETED_EVENT, onTourDone);
  }, [tryActivate]);

  // Mount / deps: tour already completed earlier (no overlay), same session on Analytics
  useEffect(() => {
    if (spotlightActiveRef.current || isFullUpdateSpotlightCompleted()) return;
    if (!eligible()) return;
    if (isWelcomeTourOverlayInDom()) return;
    tryActivate();
  }, [eligible, tryActivate]);

  // Worklogs appeared (e.g. sync elsewhere): stop highlighting
  useEffect(() => {
    if (!spotlightActiveRef.current) return;
    if (!doneCountLoaded) return;
    if (doneCount > 0) {
      dismissSpotlight();
    }
  }, [doneCount, doneCountLoaded, dismissSpotlight]);

  return { spotlightActive, dismissSpotlight };
}
